import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { ACCENT, formatMXN } from "./shared";
import { getValorEvento } from "../../utils/valorEvento";

const CuentasPorCobrar = () => {
  const navigate = useNavigate();
  const { events: allEvents } = useVenue();
  const [showAllEvents, setShowAllEvents] = useState(false);

  const allCotizados = useMemo(() => {
    if (!allEvents.length) return [];
    return allEvents
      .filter((e) => {
        if (e.status === "cancelado") return false;
        if (e.eventType !== "event") return false;
        // Con valor definido (cotización o precio acordado), o con pagos
        // registrados aunque no tenga valor — esa inconsistencia debe verse
        // en la lista, no desaparecer de ella.
        return getValorEvento(e) > 0 || Number(e.totalPagado || 0) > 0;
      })
      .map((e) => {
        const precio = getValorEvento(e);
        const pagado = Number(e.totalPagado || 0);
        let startDate = e.startTime;
        if (startDate?.toDate) startDate = startDate.toDate();
        else if (startDate?.seconds) startDate = new Date(startDate.seconds * 1000);
        else if (typeof startDate === "string" || typeof startDate === "number") startDate = new Date(startDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        let diasParaEvento = null;
        if (startDate instanceof Date && !isNaN(startDate)) {
          const eventDay = new Date(startDate);
          eventDay.setHours(0, 0, 0, 0);
          diasParaEvento = Math.ceil((eventDay - now) / (1000 * 60 * 60 * 24));
        }
        return {
          id: e.id,
          nombre: e.nombre || "Sin nombre",
          startDate,
          precioAcordado: precio,
          totalPagado: pagado,
          saldoPendiente: precio - pagado,
          porcentajePagado: precio > 0 ? Math.min(100, Math.round((pagado / precio) * 100)) : 0,
          diasParaEvento,
          pagado: precio > 0 && pagado >= precio - 0.01,
          sobrepago: precio > 0 && pagado > precio + 0.01,
          sinValor: precio <= 0,
        };
      })
      .sort((a, b) => {
        const da = a.startDate instanceof Date && !isNaN(a.startDate) ? a.startDate : new Date(9999, 0);
        const db = b.startDate instanceof Date && !isNaN(b.startDate) ? b.startDate : new Date(9999, 0);
        return da - db;
      });
  }, [allEvents]);

  // Pendientes de cobro + inconsistencias (sobrepagos) que requieren atención.
  const cuentasPorCobrar = useMemo(
    () => allCotizados.filter((e) => !e.pagado || e.sobrepago),
    [allCotizados]
  );

  const cuentasVisible = showAllEvents ? allCotizados : cuentasPorCobrar;

  if (cuentasPorCobrar.length === 0 && !showAllEvents) {
    return (
      <div className="p-4">
        <div className="text-center py-12 text-zinc-400">
          <p>No hay cuentas por cobrar. Todos los eventos están pagados.</p>
          {allCotizados.length > 0 && (
            <button
              type="button"
              onClick={() => setShowAllEvents(true)}
              className="mt-3 text-sm font-medium hover:underline"
              style={{ color: ACCENT }}
            >
              Ver todos los eventos
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex flex-wrap items-center gap-4 px-1 mb-4">
        <div>
          <div className="text-xs text-zinc-500">Total comprometido</div>
          <div className="text-lg font-bold text-zinc-900 dark:text-white">
            {formatMXN(cuentasVisible.reduce((s, e) => s + e.precioAcordado, 0))}
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-500">Cobrado</div>
          <div className="text-lg font-bold text-emerald-600">
            {formatMXN(cuentasVisible.reduce((s, e) => s + e.totalPagado, 0))}
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-500">Por cobrar</div>
          <div className="text-lg font-bold text-amber-600">
            {formatMXN(cuentasVisible.reduce((s, e) => s + Math.max(0, e.saldoPendiente), 0))}
          </div>
        </div>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setShowAllEvents(!showAllEvents)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              showAllEvents
                ? "text-white shadow-sm"
                : "text-zinc-500 bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600"
            }`}
            style={showAllEvents ? { backgroundColor: ACCENT } : undefined}
          >
            {showAllEvents ? "Todos" : "Pendientes"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60">
        <table className="min-w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[22%]" />
            <col className="w-[14%]" />
            <col className="w-[10%]" />
            <col className="w-[16%]" />
            <col className="w-[14%]" />
            <col className="w-[14%]" />
            <col className="w-[10%]" />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: "#F59E0B0d" }}>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Evento</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">Fecha del evento</th>
              <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500">Días para evento</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Valor del evento</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Pagado</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Por cobrar</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-zinc-500">Avance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
            {cuentasVisible.map((row) => (
              <tr key={row.id} className="group">
                <td className="px-3 py-2.5 overflow-hidden max-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/eventos/${row.id}`)}
                    className="text-sm font-medium transition hover:underline block truncate w-full text-left"
                    style={{ color: ACCENT }}
                    title={row.nombre}
                  >
                    {row.nombre}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                  {row.startDate instanceof Date && !isNaN(row.startDate)
                    ? row.startDate.toLocaleDateString("es-MX")
                    : "-"}
                </td>
                <td className="px-3 py-2.5 text-center whitespace-nowrap">
                  {row.diasParaEvento !== null ? (
                    <span className={`text-sm font-semibold ${
                      row.diasParaEvento < 0
                        ? "text-zinc-400"
                        : row.diasParaEvento <= 7
                        ? "text-red-600"
                        : row.diasParaEvento <= 30
                        ? "text-amber-600"
                        : "text-zinc-700 dark:text-zinc-300"
                    }`}>
                      {row.diasParaEvento < 0
                        ? `Hace ${Math.abs(row.diasParaEvento)}d`
                        : row.diasParaEvento === 0
                        ? "Hoy"
                        : `${row.diasParaEvento}d`}
                    </span>
                  ) : "-"}
                </td>
                <td className="px-3 py-2.5 text-right font-medium text-zinc-900 dark:text-white">
                  {row.sinValor ? (
                    <span className="italic font-normal text-zinc-400">Sin valor</span>
                  ) : (
                    formatMXN(row.precioAcordado)
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">
                  {formatMXN(row.totalPagado)}
                </td>
                <td className={`px-3 py-2.5 text-right font-semibold ${row.sinValor ? "text-zinc-400" : row.sobrepago ? "text-red-600" : row.pagado ? "text-emerald-600" : "text-amber-600"}`}>
                  {row.sinValor
                    ? "—"
                    : row.sobrepago
                    ? `Sobrepago ${formatMXN(row.totalPagado - row.precioAcordado)}`
                    : row.pagado
                    ? "Pagado"
                    : formatMXN(row.saldoPendiente)}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-emerald-500"
                        style={{ width: `${row.porcentajePagado}%` }}
                      />
                    </div>
                    <span className="text-xs text-zinc-500 w-8 text-right">
                      {row.porcentajePagado}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-zinc-200 dark:border-zinc-600">
              <td colSpan={3} className="px-3 py-2.5 text-sm font-semibold text-zinc-900 dark:text-white">
                Total
              </td>
              <td className="px-3 py-2.5 text-right font-semibold text-zinc-900 dark:text-white">
                {formatMXN(cuentasVisible.reduce((s, e) => s + e.precioAcordado, 0))}
              </td>
              <td className="px-3 py-2.5 text-right font-semibold text-emerald-600">
                {formatMXN(cuentasVisible.reduce((s, e) => s + e.totalPagado, 0))}
              </td>
              <td className="px-3 py-2.5 text-right font-semibold text-amber-600">
                {formatMXN(cuentasVisible.reduce((s, e) => s + Math.max(0, e.saldoPendiente), 0))}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default CuentasPorCobrar;
